import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { fetchOperationsPlannerSummary } from "../../../api/operationsPlannerSummaryService";
import { OperationsPlannerSummaryDto } from "../../../interfaces/Operations/operationsPlannerSummary";

const formatSummaryDate = (value: string) =>
  dayjs(value).format("dddd, MMMM D");

const getDifferenceColor = (difference: number) => {
  if (difference < 0) {
    return "error.main";
  }

  if (difference > 0) {
    return "warning.main";
  }

  return "text.secondary";
};

const buildMonthDates = (month: Dayjs) => {
  const startOfMonth = month.startOf("month");
  const totalDays = startOfMonth.daysInMonth();

  return Array.from({ length: totalDays }, (_, index) =>
    startOfMonth.add(index, "day"),
  );
};

const OperationsSeatedReport = () => {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf("day"));

  const selectedMonth = useMemo(
    () => selectedDate.startOf("month"),
    [selectedDate],
  );

  const monthDates = useMemo(
    () => buildMonthDates(selectedMonth),
    [selectedMonth],
  );

  const selectedDateKey = useMemo(
    () => selectedDate.format("YYYY-MM-DD"),
    [selectedDate],
  );

  const {
    data: summary,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<OperationsPlannerSummaryDto, Error>({
    queryKey: ["operations-seated-report", selectedDateKey],
    queryFn: () => fetchOperationsPlannerSummary(token, selectedDateKey),
    enabled: Boolean(token),
    placeholderData: (previousData) => previousData,
  });

  const handleMonthChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }

    const startOfMonth = value.startOf("month");
    const targetDay = Math.min(selectedDate.date(), startOfMonth.daysInMonth());
    setSelectedDate(startOfMonth.date(targetDay));
  };

  const handleShiftMonth = (offset: number) => {
    setSelectedDate((prev) => {
      const tentative = prev.add(offset, "month");
      const day = Math.min(prev.date(), tentative.daysInMonth());
      return tentative.date(day);
    });
  };

  const handleDaySelect = (value: Dayjs) => {
    setSelectedDate(value.startOf("day"));
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <EventSeatRoundedIcon color="primary" />
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Seated report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Daily projected vs actual seated counts for the selected day.
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ maxWidth: "100%" }}
        >
          <IconButton
            aria-label="Previous month"
            onClick={() => handleShiftMonth(-1)}
          >
            <ArrowBackIosNewRoundedIcon fontSize="small" />
          </IconButton>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Month"
              value={selectedMonth}
              onChange={handleMonthChange}
              views={["year", "month"]}
              format="MMMM YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: 172, sm: 220 }, maxWidth: "100%" },
                },
              }}
            />
          </LocalizationProvider>
          <IconButton
            aria-label="Next month"
            onClick={() => handleShiftMonth(1)}
          >
            <ArrowForwardIosRoundedIcon fontSize="small" />
          </IconButton>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                aria-label="Refresh report"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {isFetching && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          mb={2}
          color="text.secondary"
        >
          <CircularProgress size={18} thickness={4} />
          <Typography variant="body2">Loading latest data...</Typography>
        </Stack>
      )}

      <Box
        className="report-date-picker"
        display="flex"
        flexWrap="wrap"
        gap={1}
        mb={3}
      >
        {monthDates.map((date) => {
          const key = date.format("YYYY-MM-DD");
          const isSelected = date.isSame(selectedDate, "day");

          return (
            <Button
              key={key}
              variant={isSelected ? "contained" : "outlined"}
              color={isSelected ? "primary" : "inherit"}
              onClick={() => handleDaySelect(date)}
              size="small"
              sx={{
                minWidth: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 1,
              }}
            >
              <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                {date.format("ddd")}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {date.format("D")}
              </Typography>
            </Button>
          );
        })}
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error?.message || "Unable to load seated report."}
        </Alert>
      )}

      {!summary ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="40vh"
        >
          <Typography color="text.secondary">
            No summary data available for this date.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={formatSummaryDate(summary.date)}
                subheader={dayjs(summary.date).format("MMMM YYYY")}
              />
              <CardContent>
                {summary.sections.map((section) => {
                  const isAdditionalMetrics =
                    section.title.toLowerCase() === "additional metrics";

                  if (isAdditionalMetrics) {
                    const activeBreakdown = section.items.filter((item) =>
                      [
                        "metric-active-total",
                        "metric-rent-in-c1",
                        "metric-rent-in-fox",
                        "metric-owner-operators",
                        "metric-available",
                      ].includes(item.key),
                    );

                    const activeTotal = activeBreakdown.find(
                      (item) => item.key === "metric-active-total",
                    );
                    const activeDetails = activeBreakdown.filter(
                      (item) => item.key !== "metric-active-total",
                    );

                    const hiddenMetricKeys = [
                      "metric-total-trucks",
                      "metric-sold",
                      "metric-for-sale",
                      "metric-rent-out",
                      "metric-lease-out",
                    ];

                    const fallbackMetrics = section.items.filter(
                      (item) =>
                        !activeBreakdown.includes(item) &&
                        !hiddenMetricKeys.includes(item.key),
                    );

                    const renderMetricCard = (
                      item: OperationsPlannerSummaryDto["sections"][number]["items"][number],
                    ) => (
                      <Grid item xs={12} sm={6} md={3} key={item.key}>
                        <Box
                          p={2}
                          border={1}
                          borderColor="divider"
                          borderRadius={2}
                          height="100%"
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            textTransform="uppercase"
                            gutterBottom
                            display="block"
                          >
                            {item.label}
                          </Typography>
                          <Typography variant="h5" fontWeight={700}>
                            {item.actualSeated ?? item.projected ?? 0}
                          </Typography>
                        </Box>
                      </Grid>
                    );

                    return (
                      <Box key={`${summary.date}-${section.title}`} mb={3}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          textTransform="uppercase"
                          fontWeight={600}
                          gutterBottom
                        >
                          {section.title}
                        </Typography>

                        {activeBreakdown.length > 0 && (
                          <Box mb={2}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              gutterBottom
                            >
                              Active truck breakdown
                            </Typography>
                            <Grid container spacing={2}>
                              {activeTotal && (
                                <Grid item xs={12} md={4}>
                                  <Box
                                    p={2}
                                    border={1}
                                    borderColor="divider"
                                    borderRadius={2}
                                    height="100%"
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      textTransform="uppercase"
                                      gutterBottom
                                      display="block"
                                    >
                                      {activeTotal.label}
                                    </Typography>
                                    <Typography variant="h4" fontWeight={800}>
                                      {activeTotal.actualSeated ??
                                        activeTotal.projected ??
                                        0}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}

                              {activeDetails.map((item) => (
                                <Grid item xs={12} sm={4} md={2} key={item.key}>
                                  <Box
                                    p={2}
                                    border={1}
                                    borderColor="divider"
                                    borderRadius={2}
                                    height="100%"
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      textTransform="uppercase"
                                      gutterBottom
                                      display="block"
                                    >
                                      {item.label}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                      {item.actualSeated ?? item.projected ?? 0}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}

                        {fallbackMetrics.length > 0 && (
                          <Grid container spacing={2}>
                            {fallbackMetrics.map(renderMetricCard)}
                          </Grid>
                        )}
                      </Box>
                    );
                  }

                  return (
                    <Box key={`${summary.date}-${section.title}`} mb={3}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textTransform="uppercase"
                        fontWeight={600}
                        gutterBottom
                      >
                        {section.title}
                      </Typography>
                      <TableContainer
                        role="region"
                        aria-label="Seated report table"
                        tabIndex={0}
                      >
                        <Table sx={{ minWidth: 600 }} size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Metric</TableCell>
                              <TableCell align="right">Projected</TableCell>
                              <TableCell align="right">Actual</TableCell>
                              <TableCell align="right">Difference</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {section.items.map((item) => (
                              <TableRow key={item.key} hover>
                                <TableCell>{item.label}</TableCell>
                                <TableCell align="right">
                                  {item.projected}
                                </TableCell>
                                <TableCell align="right">
                                  {item.actualSeated}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: getDifferenceColor(item.difference),
                                  }}
                                >
                                  {item.difference}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default OperationsSeatedReport;
